import React, { useRef, useEffect, useState, useCallback } from 'react';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { motion } from 'framer-motion';
import { LanguageEngine } from '../../lib/LanguageEngine';
import { hapticTap } from '../../utils/haptics';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

interface BigButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'water' | 'medicine' | 'food' | 'exercise' | 'emergency' | 'outline';
  icon?: React.ReactNode;
  label: string;
  sublabel?: string;
  fullWidth?: boolean;
  compact?: boolean;
}

export const BigButton = ({
  className,
  variant = 'primary',
  icon,
  label,
  sublabel,
  fullWidth = false,
  compact = false,
  ...props
}: BigButtonProps) => {
  const labelRef = useRef<HTMLSpanElement>(null);
  const containerRef = useRef<HTMLButtonElement>(null);
  const [fontSize, setFontSize] = useState(compact ? 14 : 20);
  const [isReady, setIsReady] = useState(false);

  const tintedVariants = new Set(['water', 'medicine', 'food', 'exercise']);
  const isTinted = tintedVariants.has(variant);

  const variants = {
    primary: 'bg-primary text-primary-foreground hover:bg-primary-dark',
    secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary-light',
    water: 'bg-water text-text-primary hover:opacity-95',
    medicine: 'bg-medicine text-text-primary hover:opacity-95',
    food: 'bg-food text-text-primary hover:opacity-95',
    exercise: 'bg-exercise text-text-primary hover:opacity-95',
    emergency: 'bg-emergency text-emergency-foreground hover:opacity-90',
    outline: 'bg-surface text-text-primary border-2 border-primary/30 hover:border-primary hover:bg-accent/10',
  };

  // Auto-fit text to container
  const fitText = useCallback(() => {
    const labelEl = labelRef.current;
    const containerEl = containerRef.current;
    if (!labelEl || !containerEl) return;

    const text = labelEl.textContent || label;
    if (!text.trim()) return;

    // Get container width with padding
    const containerWidth = containerEl.clientWidth - 32; // Account for padding
    const maxFontSize = compact ? 14 : 20;
    const minFontSize = compact ? 10 : 12;

    // Get language-specific width ratio
    const charWidthRatio = LanguageEngine.getCharWidthRatio();

    // Calculate based on text length and character width
    const avgCharWidth = maxFontSize * 0.6 * charWidthRatio;
    const estimatedWidth = text.length * avgCharWidth;

    let newSize = maxFontSize;
    if (estimatedWidth > containerWidth * 2) { // Allow 2 lines
      const ratio = (containerWidth * 2) / estimatedWidth;
      newSize = Math.max(minFontSize, Math.floor(maxFontSize * ratio));
    }

    // Fine-tune with actual measurement
    labelEl.style.fontSize = `${newSize}px`;
    labelEl.style.whiteSpace = 'normal';
    labelEl.style.wordBreak = 'break-word';

    // If still overflowing, reduce more
    let attempts = 0;
    while (labelEl.scrollHeight > (compact ? 50 : 60) && newSize > minFontSize && attempts < 5) {
      newSize -= 1;
      labelEl.style.fontSize = `${newSize}px`;
      attempts++;
    }

    setFontSize(newSize);
    setIsReady(true);
  }, [label, compact]);

  // Initial fit
  useEffect(() => {
    const timer = setTimeout(fitText, 50);
    return () => clearTimeout(timer);
  }, [fitText, label]);

  // Refit on window resize
  useEffect(() => {
    const handleResize = () => fitText();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [fitText]);

  // Subscribe to language changes
  useEffect(() => {
    const unsubscribe = LanguageEngine.subscribe(() => {
      setTimeout(fitText, 300);
    });
    return unsubscribe;
  }, [fitText]);

  // MutationObserver for translation changes
  useEffect(() => {
    const labelEl = labelRef.current;
    if (!labelEl) return;

    const observer = new MutationObserver(() => {
      setTimeout(fitText, 100);
    });

    observer.observe(labelEl, {
      characterData: true,
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, [fitText]);

  // Handle click with haptic feedback
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    hapticTap();
    props.onClick?.(e);
  };

  return (
    <motion.button
      ref={containerRef}
      whileTap={{ scale: 0.97 }}
      whileHover={{ y: -2 }}
      onClick={handleClick}
      className={cn(
        'relative flex flex-col items-center justify-center rounded-4xl transition-all duration-300 shadow-warm',
        compact ? 'p-4 min-h-[120px]' : 'p-6 min-h-[160px]',
        variants[variant],
        fullWidth ? 'w-full' : 'w-full',
        className
      )}
      {...props}
    >
      {icon && (
        <div
          className={cn(
            'mb-3 flex items-center justify-center flex-shrink-0',
            compact ? '[&>svg]:w-10 [&>svg]:h-10' : '[&>svg]:w-12 [&>svg]:h-12',
            '[&>svg]:stroke-[2] [&>svg]:drop-shadow-sm',
            isTinted && '[&>svg]:text-white'
          )}
        >
          {icon}
        </div>
      )}
      <span 
        ref={labelRef}
        className={cn(
          "font-bold text-center leading-tight px-1 w-full",
          compact ? 'max-h-[50px]' : 'max-h-[60px]'
        )}
        style={{ 
          fontSize: `${fontSize}px`,
          lineHeight: 1.2,
          overflow: 'hidden',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          wordBreak: 'break-word',
          hyphens: 'auto',
          opacity: isReady ? 1 : 0,
          transition: 'opacity 0.15s ease-out',
        }}
      >
        {label}
      </span>
      {sublabel && (
        <span 
          className="mt-2 text-sm opacity-80 text-center font-normal px-2 line-clamp-1"
          style={{ 
            wordBreak: 'break-word',
            maxWidth: '100%',
          }}
        >
          {sublabel}
        </span>
      )}
    </motion.button>
  );
};
